import { useEffect, useState, type ReactNode } from 'react';
import { Inbox } from 'lucide-react';
import { arrivedLine, offerArrived, readArrived, type ArrivedSummary } from './arrived-setup';
import { readOnlyView } from './read-only';
import type { UploadChannel } from './upload';

interface ArrivedOfferProps {
  channel: UploadChannel;
  /** The job is on its setup: the only time documents wait for one. */
  inSetup: boolean;
}

/**
 * One line over a job that is being set up while documents a program sent wait for it (arrived-setup.ts):
 * how many pages arrived, and the button that sets the job up from them. Nothing is shown when nothing
 * waits, to a person who may not set the job up (the API answers them nothing), or in a read-only view.
 */
export function ArrivedOffer({ channel, inSetup }: ArrivedOfferProps): ReactNode {
  const [arrived, setArrived] = useState<ArrivedSummary | null>(null);
  const [taken, setTaken] = useState(false);
  const { engineOrigin, tenant } = channel;
  useEffect(() => {
    if (!inSetup || readOnlyView()) return undefined;
    let current = true;
    void readArrived({ engineOrigin, tenant }).then((summary) => {
      if (current) setArrived(summary);
    });
    return () => {
      current = false;
    };
  }, [engineOrigin, tenant, inSetup]);
  if (!inSetup || taken || !arrived || arrived.pages === 0) return null;
  return (
    <div className="paige-upload-progress paige-arrived-offer" role="status" aria-live="polite">
      <Inbox size={15} aria-hidden="true" />
      <span>{arrivedLine(arrived)}</span>
      <button
        type="button"
        onClick={() => {
          setTaken(true);
          offerArrived(arrived.files);
        }}
      >
        Set up from these documents
      </button>
    </div>
  );
}
